/*eslint no-magic-numbers: ["error", { "ignore": [0, 3, 1] }]*/
import React, { useContext, useState, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'

import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import Input from 'components/Input'
import Select from 'components/Select'
import Textarea from 'components/Textarea'
import { delay } from 'utils/delay'
import { requestCreateSiteEcoSystem, requestUpdateSiteEcoSystem } from 'api/ecoSystem'
import { redListOfEcosystemCategory } from 'constants/redListOfEcosystemCategory'
import { defaultSiteProposalValidationResults } from 'constants/defaultSiteProposalValidationResults'
import { SiteContext } from 'providers/site'
import { SITE_STATUS_DRAFT_ID } from 'constants/siteStatuses'
import { reasonDefaultOptions, reasonOptionsIrreversibleSelected, reasonOptionsReversibleSelected, IrreversibleIndex, ReversibleIndex } from 'constants/rejectTriggerReason'

// Create ecosystem-specific options for mutual exclusivity
const reasonQualifyOptions = [
  { label: 'Qualifies', value: 1, disabled: false },
  { label: 'Improved site information', value: 2, disabled: true },
  { label: 'KBA criteria revision', value: 3, disabled: true },
  { label: 'Revision to Red List status', value: 4, disabled: true },
  { label: 'Revision of global threshold', value: 5, disabled: true },
  { label: 'No longer geographically restricted', value: 6, disabled: true },
  { label: 'Genuine decline, irreversible', value: 7, disabled: true },
  { label: 'Genuine decline, reversible', value: 8, disabled: true },
  { label: 'Genuine decline, other factors', value: 9, disabled: true }
]

const reasonNonQualifyOptions = [
  { label: 'Qualifies', value: 1, disabled: true },
  { label: 'Improved site information', value: 2, disabled: false },
  { label: 'KBA criteria revision', value: 3, disabled: false },
  { label: 'Revision to Red List status', value: 4, disabled: false },
  { label: 'Revision of global threshold', value: 5, disabled: false },
  { label: 'No longer geographically restricted', value: 6, disabled: false },
  { label: 'Genuine decline, irreversible', value: 7, disabled: false },
  { label: 'Genuine decline, reversible', value: 8, disabled: false },
  { label: 'Genuine decline, other factors', value: 9, disabled: false }
]
import { EcosystemContentFooter } from './style'
import MultiSelectionDropdown from 'components/MultiSelectDropdown'
import CheckIcon from 'assets/icons/check-icon'
import Button from 'components/Button'
import { isValidSixDecimalNumber, noZeroAfterSixDecimal } from 'utils/isValidSixDecimalNumber'
import formatNum2Dp from 'utils/formatNum2DP'

const inputSaveDelayTime = 300
const updateEcosystemNameTimeOut = delay(inputSaveDelayTime)
// const updateEcosystemNumberTimeOut = delay(inputSaveDelayTime)
const updateEcosystemGlobalExtentTimeOut = delay(inputSaveDelayTime)
const updateEcosystemExtentAtSiteMinTimeOut = delay(inputSaveDelayTime)
const updateEcosystemExtentAtSiteBestTimeOut = delay(inputSaveDelayTime)
const updateEcosystemExtentAtSiteMaxTimeOut = delay(inputSaveDelayTime)
const updateEcosystemAssessmentDateTimeOut = delay(inputSaveDelayTime)
const updateEcosystemDataSourceTimeOut = delay(inputSaveDelayTime)

const EcosystemForm = ({ item: ecosystem, updateEcoSystem, siteId, toggleAccordion, defaultPage = false, getEcosystems }) => {
  const params = useParams()
  const checkedValue = useRef([])
  const { site, siteProposalFieldValidation, isEditable } = useContext(SiteContext)

  const checkFields = siteProposalFieldValidation?.ecoSystem?.[ecosystem.id]
    ? siteProposalFieldValidation.ecoSystem[ecosystem.id]
    : defaultSiteProposalValidationResults.ecoSystem


  const [ecoSystemName, setEcoSystemName] = useState(ecosystem.name)
  // const [codeNumber, setCodeNumber] = useState(ecosystem.number)
  const [ecosystemRedListId, setEcosystemRedListId] = useState(
    redListOfEcosystemCategory.find((item) => {
      return item.value === ecosystem.EcoSystemRedListId
    }) || null
  )

  const [globalExtent, setGlobalExtent] = useState(ecosystem.globalExtent ? formatNum2Dp(ecosystem.globalExtent) : '')
  const [extent, setExtent] = useState(ecosystem.extentAtSiteMin ? formatNum2Dp(ecosystem.extentAtSiteMin) : '')
  const [extentBest, setExtentBest] = useState(ecosystem.extentAtSiteBest ? formatNum2Dp(ecosystem.extentAtSiteBest) : '')
  const [extentMax, setExtentMax] = useState(ecosystem.extentAtSiteMax ? formatNum2Dp(ecosystem.extentAtSiteMax) : '')
  const [assessmentDate, setAssessmentDate] = useState(ecosystem.assessmentDate)
  const [dataSource, setDataSource] = useState(ecosystem.dataSource)
  const [qualificationReason, setQualificationReason] = useState(ecosystem.triggerReasonId?.map((item) => {
    if (item > 3) {
      return item - 1
    }
    return item
  }))

  const [reassessmentRational, setReassessmentRational] = useState(ecosystem.triggerReasonText ? ecosystem.triggerReasonText : '')
  const [checkedOptions, setCheckedOptions] = useState(reasonDefaultOptions)

  const qualified = ecosystem.reassessmentFlag ? ecosystem.triggerReasonId.some((element) => {
    // eslint-disable-next-line no-magic-numbers
    return element === 1
  }) : true

  const disabled = site.SiteStatusId !== SITE_STATUS_DRAFT_ID || (!isEditable && site.SiteStatusId === SITE_STATUS_DRAFT_ID) || !qualified

  function handleChangeValueChecked(Ids) {
    checkedValue.current = Ids
    const qualifyIndex = 0 // "Qualifies" is at index 0
    const isAnyNonQualifyOptionsSelected = Ids.some((item) => {
      return item.checked && item.value !== 1
    })

    if (Ids[IrreversibleIndex].checked) {
      setCheckedOptions(reasonOptionsIrreversibleSelected)
    } else if (Ids[ReversibleIndex].checked) {
      setCheckedOptions(reasonOptionsReversibleSelected)
    } else if (Ids[qualifyIndex].checked) {
      setCheckedOptions(reasonQualifyOptions)
    } else if (isAnyNonQualifyOptionsSelected) {
      setCheckedOptions(reasonNonQualifyOptions)
    } else {
      setCheckedOptions(reasonDefaultOptions)
    }
  }

  function handleChangeQualificationReason(e) {
    setQualificationReason(e)

    const formattedReasons = e.map((item) => {
      if (item > 3) {
        return item + 1
      }
      return item
    })
    requestUpdateSiteEcoSystem(ecosystem.id, params.siteHistoryId, { triggerReasonId: formattedReasons }).then((updatedEcoSystem) => {
      updateEcoSystem(updatedEcoSystem)
      getEcosystems()
    })
  }

  function handleChangeReassessmentRational(e) {
    setReassessmentRational(e.target.value)

    requestUpdateSiteEcoSystem(ecosystem.id, params.siteHistoryId, { triggerReasonText: e.target.value }).then((updatedEcoSystem) => {
      updateEcoSystem(updatedEcoSystem)
      getEcosystems()
    })
  }

  async function createAndUpdate(element) {
    const data = await requestCreateSiteEcoSystem(siteId, params.siteHistoryId)
    const updatedEcoSystem = await requestUpdateSiteEcoSystem(data.id, params.siteHistoryId, element)
    updateEcoSystem(updatedEcoSystem)
    await getEcosystems()
  }

  const handleChangeEcosystemName = (e) => {
    setEcoSystemName(e.target.value)
  }

  const handleOnBlurChangeEcosystemName = (e) => {
    setEcoSystemName(e.target.value)

    updateEcosystemNameTimeOut(() => {
      if (ecosystem.id === '') {
        createAndUpdate({ name: e.target.value })
      } else {
        requestUpdateSiteEcoSystem(ecosystem.id, params.siteHistoryId, { name: e.target.value }).then((updatedEcoSystem) => {
          updateEcoSystem(updatedEcoSystem)
          getEcosystems()
        })
      }

    })
  }

  function handleChangeEcosystemRedListId(e) {
    setEcosystemRedListId(e)

    if (ecosystem.id === '') {
      return createAndUpdate({ EcoSystemRedListId: e.value })
    }

    requestUpdateSiteEcoSystem(ecosystem.id, params.siteHistoryId, { EcoSystemRedListId: e.value }).then((updatedEcoSystem) => {
      updateEcoSystem(updatedEcoSystem)
      getEcosystems()
    })
  }

  function handleChangeGlobalExtent(e) {
    if (isValidSixDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterSixDecimal(e.target.value)) {
        setGlobalExtent(e.target.value)
      }
    }
  }

  function handleOnBlurChangeGlobalExtent(e) {
    if (isValidSixDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterSixDecimal(e.target.value)) {
        setGlobalExtent(e.target.value)
      }

      if (ecosystem.id === '') {
        return createAndUpdate({ globalExtent: e.target.value === '' ? 0 : Number(e.target.value).toFixed('10') })
      }
      const value = Number(parseFloat(e.target.value).toFixed('10'))
      updateEcosystemGlobalExtentTimeOut(() => {
        requestUpdateSiteEcoSystem(ecosystem.id, params.siteHistoryId, { globalExtent: e.target.value === '' ? 0 : value }).then((updatedEcoSystem) => {
          updateEcoSystem(updatedEcoSystem)
          getEcosystems()
        })
      })
    }
  }

  function handleChangeExtent(e) {
    if (isValidSixDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterSixDecimal(e.target.value)) {
        setExtent(e.target.value)
      }
    }
  }

  function handleOnBlurChangeExtent(e) {
    if (isValidSixDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterSixDecimal(e.target.value)) {
        setExtent(e.target.value)
      }

      if (ecosystem.id === '') {
        return createAndUpdate({ extentAtSiteMin: e.target.value === '' ? 0 : Number(e.target.value) })
      }
      const value = Number(parseFloat(e.target.value).toFixed('10'))
      updateEcosystemExtentAtSiteMinTimeOut(() => {
        requestUpdateSiteEcoSystem(ecosystem.id, params.siteHistoryId, { extentAtSiteMin: e.target.value === '' ? 0 : value }).then((updatedEcoSystem) => {
          updateEcoSystem(updatedEcoSystem)
          getEcosystems()
        })
      })
    }
  }

  function handleChangeExtentBest(e) {
    if (isValidSixDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterSixDecimal(e.target.value)) {
        setExtentBest(e.target.value)
      }
    }
  }

  function handleOnBlurChangeExtentBest(e) {
    if (isValidSixDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterSixDecimal(e.target.value)) {
        setExtentBest(e.target.value)
      }

      if (ecosystem.id === '') {
        return createAndUpdate({ extentAtSiteBest: e.target.value === '' ? 0 : Number(e.target.value) })
      }
      const value = Number(parseFloat(e.target.value).toFixed('10'))
      updateEcosystemExtentAtSiteBestTimeOut(() => {
        requestUpdateSiteEcoSystem(ecosystem.id, params.siteHistoryId, { extentAtSiteBest: e.target.value === '' ? 0 : value }).then((updatedEcoSystem) => {
          updateEcoSystem(updatedEcoSystem)
          getEcosystems()
        })
      })
    }
  }

  function handleChangeExtentMax(e) {
    if (isValidSixDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterSixDecimal(e.target.value)) {
        setExtentMax(e.target.value)
      }
    }
  }

  function handleOnBlurChangeExtentMax(e) {
    if (isValidSixDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterSixDecimal(e.target.value)) {
        setExtentMax(e.target.value)
      }

      if (ecosystem.id === '') {
        return createAndUpdate({ extentAtSiteMax: e.target.value === '' ? 0 : Number(e.target.value) })
      }
      const value = Number(parseFloat(e.target.value).toFixed('10'))
      updateEcosystemExtentAtSiteMaxTimeOut(() => {
        requestUpdateSiteEcoSystem(ecosystem.id, params.siteHistoryId, { extentAtSiteMax: e.target.value === '' ? 0 : value }).then((updatedEcoSystem) => {
          updateEcoSystem(updatedEcoSystem)
          getEcosystems()
        })
      })
    }
  }

  function handleChangeAssessmentDate(e) {
    const currentYear = new Date().getFullYear()
    const passedValue = e.target.value
    // eslint-disable-next-line no-magic-numbers
    if (passedValue <= currentYear && !passedValue.includes('.') && !passedValue.includes(' ') && (passedValue > 0 || passedValue === '')) {
      setAssessmentDate(passedValue)
    }
    // eslint-disable-next-line no-magic-numbers
    if (passedValue <= currentYear && passedValue > 1900 && passedValue.length === 4) {
      updateEcosystemAssessmentDateTimeOut(() => {
        requestUpdateSiteEcoSystem(ecosystem.id, params.siteHistoryId, { assessmentDate: e.target.value ? (e.target.value) : null }).then(
          (updatedEcoSystem) => {
            updateEcoSystem(updatedEcoSystem)
            getEcosystems()
          }
        )
      })
    }
  }

  function handleChangeDataSource(e) {
    setDataSource(e.target.value)

    if (ecosystem.id === '') {
      return createAndUpdate({ dataSource: e.target.value })
    }

    updateEcosystemDataSourceTimeOut(() => {
      requestUpdateSiteEcoSystem(ecosystem.id, params.siteHistoryId, { dataSource: e.target.value }).then((updatedEcoSystem) => {
        updateEcoSystem(updatedEcoSystem)
        getEcosystems()
      })
    })
  }

  function handleOnBlurChangeDataSource(e) {
    setDataSource(e.target.value)

    if (ecosystem.id === '') {
      return createAndUpdate({ dataSource: e.target.value })
    }

    updateEcosystemDataSourceTimeOut(() => {
      requestUpdateSiteEcoSystem(ecosystem.id, params.siteHistoryId, { dataSource: e.target.value }).then((updatedEcoSystem) => {
        updateEcoSystem(updatedEcoSystem)
        getEcosystems()
      })
    })
  }

  return (
    <Container width='100%' padding='0px'>
      <div data-test='ecosystem-form'>
        <Row>
          <Col md={6}>
            <Input
              value={ecoSystemName}
              field='ecoSystemName'
              onChange={handleChangeEcosystemName}
              onBlur={handleOnBlurChangeEcosystemName}
              placeholder='enterEcosystemName'
              label='ecosystemName'
              infoTooltip='ecosystemNameHint'
              disabled={disabled}
              error={
                checkFields.name
                  ? { field: '', error: '' }
                  : {
                    field: 'ecoSystemName',
                    error: 'pleaseCompleteThisField'
                  }
              }
            />
          </Col>
          <Col md={6}>
            <Select
              value={ecosystemRedListId}
              onChange={handleChangeEcosystemRedListId}
              placeholder='selectEcosystemCategory'
              label='ecosystemCategory'
              field='EcoSystemRedListId'
              infoTooltip='ecosystemCategoryHint'
              options={redListOfEcosystemCategory}
              disabled={disabled}
              error={
                checkFields.EcoSystemRedListId
                  ? { field: '', error: '' }
                  : {
                    field: 'EcoSystemRedListId',
                    error: 'pleaseCompleteThisField'
                  }
              }
            />
          </Col>
        </Row>
        <Row>
          {(ecosystem.lastReassessmentDate !== null && site.reassessmentFlag && !defaultPage) && (
            <Col md={6}>
              <MultiSelectionDropdown
                value={qualificationReason}
                saveChanges={handleChangeQualificationReason}
                valueChecked={handleChangeValueChecked}
                label='triggerEcosystemLabel'
                infoTooltip='triggerLabelHint'
                field='qualificationReason'
                error={qualificationReason?.length === 0 ? {
                  field: 'qualificationReason',
                  error: 'pleaseCompleteThisField'
                } : { field: '', error: '' }}
                secondStepOptions={checkedOptions}
                placeholder='ecosystemTriggerPlaceholder'
              />
            </Col>
          )}
          <Col md={6}>
            <Input
              value={globalExtent}
              onChange={handleChangeGlobalExtent}
              onBlur={handleOnBlurChangeGlobalExtent}
              placeholder='enterGlobalExtent'
              label='ecosystemGlobalExtent'
              field='globalExtent'
              infoTooltip='ecosystemGlobalExtentHint'
              disabled={disabled}
              error={
                checkFields.globalExtent
                  ? { field: '', error: '' }
                  : {
                    field: 'globalExtent',
                    error: 'pleaseCompleteThisField'
                  }
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Input
              value={extent}
              onChange={handleChangeExtent}
              onBlur={handleOnBlurChangeExtent}
              placeholder='enterSiteMinExtent'
              label='ecosystemExtent'
              field='extentAtSiteMin'
              infoTooltip='ecosystemExtentHint'
              disabled={disabled}
              isOptional={true}
            />
          </Col>
          <Col md={6}>
            <Input
              value={extentBest}
              onChange={handleChangeExtentBest}
              onBlur={handleOnBlurChangeExtentBest}
              placeholder='enterSiteBestEstimateExtent'
              label='ecosystemExtentBest'
              field='extentAtSiteBest'
              infoTooltip='ecosystemExtentBestHint'
              disabled={disabled}
              error={
                checkFields.extentAtSiteBest
                  ? { field: '', error: '' }
                  : {
                    field: 'extentAtSiteBest',
                    error: 'pleaseCompleteThisField'
                  }
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Input
              value={extentMax}
              onChange={handleChangeExtentMax}
              onBlur={handleOnBlurChangeExtentMax}
              placeholder='enterSiteMaxExtent'
              label='ecosystemExtentMax'
              field='extentAtSiteMax'
              infoTooltip='ecosystemExtentMaxHint'
              disabled={disabled}
              isOptional={true}
            />
          </Col>
          <Col md={6}>
            <Input
              value={assessmentDate}
              onChange={handleChangeAssessmentDate}
              placeholder='assessmentDateEcoPlaceholder'
              label='assessmentDateEco'
              field='assessmentDate'
              infoTooltip='ecosystemAssessmentDateHint'
              disabled={disabled}
              error={
                checkFields.assessmentDate
                  ? { field: '', error: '' }
                  : {
                    field: 'assessmentDate',
                    error: 'pleaseCompleteThisField'
                  }
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Textarea
              value={dataSource}
              onChange={handleChangeDataSource}
              onBlur={handleOnBlurChangeDataSource}
              placeholder='enterSourceOfEcosystemData'
              label='source'
              field='ecoSystemDataSource'
              infoTooltip='ecosystemSourceHint'
              height='148px'
              disabled={disabled}
              error={
                checkFields.dataSource
                  ? { field: '', error: '' }
                  : {
                    field: 'ecoSystemDataSource',
                    error: 'pleaseCompleteThisField'
                  }
              }
            />
          </Col>
        </Row>
        {(ecosystem.lastReassessmentDate !== null && site.reassessmentFlag) && (
          <Row>
            <Col md={12}>
              <Textarea
                value={reassessmentRational}
                onChange={handleChangeReassessmentRational}
                placeholder='justificationNoChangeEcoSystemPlaceholder'
                label='justificationNoChange'
                infoTooltip='qualificationReasonHint'
                field='rationalForReassessment'
                height='128px'
                disabled={disabled}
              />
            </Col>
          </Row>
        )}
        {(ecosystem.reassessmentFlag && site.SiteStatusId === SITE_STATUS_DRAFT_ID && !defaultPage) && (
          <Row>
            <Col md={12}>
              <EcosystemContentFooter>
                <Button height='40px' isDisabled={!ecosystem.triggerReasonId.length > 0} onClick={() => {
                  toggleAccordion()
                }}
                  type={ecosystem.triggerReasonId.length > 0 ? 'primary' : 'disabledPrimary'}>
                  <CheckIcon />
                  <FormattedMessage id='finishEditing' />
                </Button>
              </EcosystemContentFooter>
            </Col>
          </Row>
        )}
        {(!ecosystem.reassessmentFlag && site.SiteStatusId === SITE_STATUS_DRAFT_ID && !defaultPage) && (
          <Row>
            <Col md={12}>
              <EcosystemContentFooter>
                <Button height='40px' onClick={toggleAccordion}
                  type='primary'>
                  <CheckIcon />
                  <FormattedMessage id='finishEditing' />
                </Button>
              </EcosystemContentFooter>
            </Col>
          </Row>
        )}
      </div>
    </Container>
  )
}

export default EcosystemForm